import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import { Modal, Button } from "antd";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  LineChart,
} from "recharts";
import Body from "../../../Assets/Images/Svg/Bodybattery.svg";

// Loader Component
const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const BODY_BATTERY_GOAL = 100; // define your goal for green color

const BatteryData = ({ smartWatchId }) => {
  const [dailies, setDailies] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [batterySummary, setBatterySummary] = useState(0);

  const patJson = sessionStorage.getItem("patientData");
  const patObj = patJson ? JSON.parse(patJson) : null;

  useEffect(() => {
    if (smartWatchId) fetchDailyBattery();
  }, [smartWatchId]);

  const fetchDailyBattery = async () => {
    const today = dayjs().format("DD/MM/YYYY");
    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: today,
        toDate: today,
        type: "DAILIES",
      });

      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        setDailies(raw);
      } else {
        setDailies({});
      }
      const res2 = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: today,
        toDate: today,
        type: "STRESSDETAILS",
      });
      if (res2?.data?.length > 0) {
        const raw2 = JSON.parse(res2.data[0].stressDetails || "{}");
        setBatterySummary(raw2?.BodyBatterySummary?.CurrentBodyBattery || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openGraph = () => setIsModalOpen(true);
  const closeGraph = () => setIsModalOpen(false);

  return (
    <>
      <div
        title="Click to view Graph"
        className="bg-white shadow rounded-lg p-4 w-full h-40 cursor-pointer hover:shadow-lg transition"
        onClick={openGraph}
      >
        <div className="flex items-center gap-2 mb-4">
          <img src={Body} alt="Body Battery" className="w-5 h-5" />
          <h2 className="text-sm font-semibold">Body Battery</h2>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <div className="w-3/4 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-horizontal-pulse"></div>
            </div>
            <p className="mt-3 text-gray-600 text-sm font-medium">Loading...</p>
          </div>
        ) : (
          <div className="flex justify-center mt-4">
            <div className="relative w-20 h-20">
              <svg
                className="w-full h-full rotate-[-90deg]"
                viewBox="0 0 36 36"
              >
                {/* Background circle */}
                <path
                  className="stroke-gray-200"
                  strokeWidth="3"
                  fill="none"
                  d="M18 2.0845
         a 15.9155 15.9155 0 1 1 0 31.831
         a 15.9155 15.9155 0 1 1 0-31.831"
                />

                {/* Progress circle */}
                <path
                  className={
                    batterySummary >= BODY_BATTERY_GOAL
                      ? "stroke-green-500"
                      : "stroke-blue-500"
                  }
                  strokeWidth="3"
                  strokeDasharray={`${batterySummary}, 100`}
                  strokeLinecap="round"
                  fill="none"
                  d="M18 2.0845
         a 15.9155 15.9155 0 1 1 0 31.831
         a 15.9155 15.9155 0 1 1 0-31.831"
                />
              </svg>

              {/* Center text */}
              <span className="absolute inset-0 flex items-center justify-center text-lg font-semibold">
                {batterySummary}
              </span>
            </div>
          </div>
        )}
      </div>

      {smartWatchId && (
        <BodyBatteryModal
          open={isModalOpen}
          onClose={closeGraph}
          smartWatchId={smartWatchId}
        />
      )}
    </>
  );
};

const BodyBatteryModal = ({ open, onClose, smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [batterySummary, setBatterySummary] = useState(0);

  const fetchDataByRange = async (from, to) => {
    const start = from.format("DD/MM/YYYY");
    const end = to.format("DD/MM/YYYY");
    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 100,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "DAILIES",
      });

      if (res?.data?.length > 0) {
        const parsed = res.data.map((item) => {
          let raw = {};
          try {
            raw = JSON.parse(item.RAW_JSON || "{}");
          } catch { }
          return {
            date: raw.calendarDate,
            charged: raw.bodyBatteryChargedValue ?? 0,
            drained: raw.bodyBatteryDrainedValue ?? 0,
            total:
              (raw.bodyBatteryChargedValue ?? 0) * 10 -
              (raw.bodyBatteryDrainedValue ?? 0) * 10,
          };
        });
        parsed.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
        setChartData(parsed);
      } else setChartData([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const build1DChartData = (raw) => {
    const values = raw?.timeOffsetBodyBatteryValues || {};

    return Object.entries(values)
      .filter(([_, v]) => v !== -1 && v !== -2)
      .map(([sec, value]) => {
        const hours = Number(sec) / 3600;

        if (hours < 0 || hours > 24) return null;

        return {
          time: hours,
          value,
        };
      })
      .filter(Boolean);
  };

  const [dataForChart, setDataForChart] = useState([]);
  const fetch1DData = async (date = dayjs()) => {
    const formatted = date.format("DD/MM/YYYY");
    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: formatted,
        toDate: formatted,
        type: "DAILIES",
      });
      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        setChartData([
          {
            date: raw.calendarDate,
            charged: raw.bodyBatteryChargedValue ?? 0,
            drained: raw.bodyBatteryDrainedValue ?? 0,
            total:
              (raw.bodyBatteryChargedValue ?? 0) * 10 -
              (raw.bodyBatteryDrainedValue ?? 0) * 10,
          },
        ]);
      } else setChartData([]);

      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");

        const chartPoints = build1DChartData(raw);

        setDataForChart(chartPoints);
      } else {
        setDataForChart([]);
      }

      const res2 = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: formatted,
        toDate: formatted,
        type: "STRESSDETAILS",
      });
      if (res2?.data?.length > 0) {
        const raw2 = JSON.parse(res2.data[0].stressDetails || "{}");
        setBatterySummary(raw2?.BodyBatterySummary?.CurrentBodyBattery || 0);
      }

      if (res2?.data?.length > 0) {
        const raw = JSON.parse(res2.data[0].RAW_JSON || "{}");

        const chartPoints = build1DChartData(raw);

        setDataForChart(chartPoints);
      } else {
        setDataForChart([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") fetch1DData(selectedDate);
    else fetchDataByRange(startDate, endDate);
  }, [tab, selectedDate, startDate, endDate, smartWatchId]);

  const handlePrevDay = () => {
    const newDate = selectedDate.subtract(1, "day");
    setSelectedDate(newDate);
    fetch1DData(newDate);
  };

  const handleNextDay = () => {
    const today = dayjs();
    const newDate = selectedDate.add(1, "day");
    if (!newDate.isAfter(today, "day")) {
      setSelectedDate(newDate);
      fetch1DData(newDate);
    }
  };

  const handlePrev7D = () => {
    const newStart = startDate.subtract(7, "day");
    const newEnd = endDate.subtract(7, "day");
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchDataByRange(newStart, newEnd);
  };

  const handleNext7D = () => {
    const today = dayjs();
    let newStart = startDate.add(7, "day");
    let newEnd = endDate.add(7, "day");
    if (newEnd.isAfter(today, "day")) {
      newEnd = today;
      newStart = today.subtract(6, "day");
    }
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchDataByRange(newStart, newEnd);
  };

  const avgCharged =
    chartData.reduce((sum, d) => sum + (d.charged ?? 0), 0) /
    (chartData.length || 1);
  const avgDrained =
    chartData.reduce((sum, d) => sum + (d.drained ?? 0), 0) /
    (chartData.length || 1);
  const avgTotal =
    chartData.reduce((sum, d) => sum + (d.total ?? 0), 0) /
    (chartData.length || 1);
  const xAxisProps = {
    dataKey: "time",
    type: "number",
    domain: [0, 24],
    ticks: [0, 4, 8, 12, 16, 20, 24],
    tickFormatter: (v) => {
      const map = {
        0: "12 AM",
        4: "4 AM",
        8: "8 AM",
        12: "12 PM",
        16: "4 PM",
        20: "8 PM",
        24: "12 AM",
      };
      return map[v] ?? "";
    },
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title="Body Battery Summary"
    >
      {loading && <Loader />}
      {/* Tabs */}
      <div className="flex justify-center mb-4 gap-3">
        <button
          onClick={() => setTab("1D")}
          className={`${tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
            } px-4 py-2 rounded-md`}
        >
          1D
        </button>
        <button
          className={`${tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
            } px-4 py-2 rounded-md`}
          onClick={() => setTab("7D")}
        >
          7D
        </button>
      </div>

      {tab === "1D" && (
        <div className="flex items-center justify-center gap-4 mb-4">
          <button
            className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
            onClick={handlePrevDay}
          >
            ← Prev Day
          </button>
          <span className="font-semibold">
            {selectedDate.format("DD/MM/YYYY")}
          </span>
          <button
            className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
            onClick={handleNextDay}
          >
            Next Day →
          </button>
        </div>
      )}
      {tab === "7D" && (
        <div className="flex justify-between mb-4">
          <button
            className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
            onClick={handlePrev7D}
          >
            ← Prev 7 Days
          </button>
          <span>
            {startDate.format("DD/MM/YYYY")} - {endDate.format("DD/MM/YYYY")}
          </span>
          <button
            className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
            onClick={handleNext7D}
          >
            Next 7 Days →
          </button>
        </div>
      )}

      {/* 1D Circle Chart */}
      {tab === "1D" && chartData.length > 0 && (
        <>
          <div className="flex justify-center mt-6">
            <div className="relative w-20 h-20">
              <svg
                className="w-full h-full rotate-[-90deg]"
                viewBox="0 0 36 36"
              >
                {/* Background circle */}
                <path
                  className="stroke-gray-200"
                  strokeWidth="3"
                  fill="none"
                  d="M18 2.0845
         a 15.9155 15.9155 0 1 1 0 31.831
         a 15.9155 15.9155 0 1 1 0-31.831"
                />

                {/* Progress circle */}
                <path
                  className={
                    batterySummary >= BODY_BATTERY_GOAL
                      ? "stroke-green-500"
                      : "stroke-blue-500"
                  }
                  strokeWidth="3"
                  strokeDasharray={`${batterySummary}, 100`}
                  strokeLinecap="round"
                  fill="none"
                  d="M18 2.0845
         a 15.9155 15.9155 0 1 1 0 31.831
         a 15.9155 15.9155 0 1 1 0-31.831"
                />
              </svg>

              {/* Center text */}
              <span className="absolute inset-0 flex items-center justify-center text-lg font-semibold">
                {batterySummary}
              </span>
            </div>
          </div>{" "}
          <div className="flex justify-around mt-5 text-center">
            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {" + " + chartData[0]?.charged.toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Charged</p>
            </div>
            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {" - " + chartData[0]?.drained.toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Drained</p>
            </div>
          </div>
          <div>
            {" "}
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dataForChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...xAxisProps} />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="value" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* 7D Bar Chart */}
      {tab === "7D" && chartData.length > 0 && (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <XAxis
                dataKey="date"
                tickFormatter={(d) => dayjs(d).format("DD/MM")}
              />
              <YAxis />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "charged") return [`${value}`, "Charged"];
                  if (name === "drained") return [`${value}`, "Drained"];
                  return [value, name];
                }}
              />
              <Legend />

              {/* Charged line - filled dot */}
              <Line
                type="monotone"
                dataKey="charged"
                stroke="#36a2eb"
                strokeWidth={2}
                dot={{ r: 5, fill: "#36a2eb" }}
                activeDot={{ r: 7 }}
              />

              {/* Drained line - hollow dot */}
              <Line
                type="monotone"
                dataKey="drained"
                stroke="#ff6384"
                strokeWidth={2}
                dot={{ r: 5, fill: "#fff", stroke: "#ff6384", strokeWidth: 2 }}
                activeDot={{ r: 7, fill: "#ff6384" }}
              />
            </LineChart>
          </ResponsiveContainer>

          {/* 7D Average Cards */}
          <div className="flex justify-around mt-5 text-center">
            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {" + " + avgCharged.toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Avg Charged</p>
            </div>
            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {" - " + avgDrained.toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Avg Drained</p>
            </div>
            <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
              <p className="text-blue-600 font-semibold text-base">
                {avgTotal.toFixed(0)}
              </p>
              <p className="text-gray-500 text-xs">Avg Total</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5 mt-6">
            {chartData.map((item, idx) => {
              const dayName = dayjs(item.date).format("ddd");
              const total = item.total ?? 0;
              const charged = item.charged ?? 0;
              const drained = item.drained ?? 0;

              return (
                <div
                  key={idx}
                  className="bg-white shadow rounded-lg p-3 flex flex-col items-center gap-2"
                >
                  <p className="text-xs text-gray-500">{dayName}</p>

                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                        {charged}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">High</p>
                    </div>

                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 border-2 border-red-500 rounded-full flex items-center justify-center text-red-500 text-xs font-semibold">
                        {drained}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Low</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rounded SVG Avg Total */}
          {/* <div className="flex justify-center mt-6">
            <div className="relative w-36 h-36">
              <svg className="w-full h-full" viewBox="0 0 36 36">
                <path
                  className="stroke-gray-200"
                  strokeWidth="4"
                  fill="none"
                  d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
                />
                <path
                  className={
                    avgTotal >= BODY_BATTERY_GOAL
                      ? "stroke-green-500"
                      : "stroke-blue-500"
                  }
                  strokeWidth="4"
                  strokeDasharray={`${avgTotal}, ${BODY_BATTERY_GOAL}`}
                  strokeLinecap="round"
                  fill="none"
                  d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-semibold">
                  {avgTotal.toFixed(0)}
                </span>
                <span className="text-xs text-gray-500">Avg Body Battery</span>
              </div>
            </div>
          </div> */}
        </>
      )}
    </Modal>
  );
};

export default BatteryData;
